import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Fuel, Gauge, GitFork } from "lucide-react";
import type { CfaoVehicle } from "@/data/cfaoVehicles";
import { formatKm, formatPrice } from "@/lib/format";
import { contactEnquiryLink } from "@/config";

export const FeaturedVehicleCard: React.FC<{ vehicle: CfaoVehicle }> = ({ vehicle: v }) => {
  const available = v.status === "published";
  const vehiclePath = `/vehicles/${v.slug}/`;
  const subject = `Vehicle enquiry: ${v.fullTitle}`;
  const stockNumber = v.stockNumber;
  const message = [
    `Subject: ${subject}`,
    "",
    "Hi Simphiwe,",
    "",
    "I am interested in this vehicle and would like to confirm its availability and arrange the next step.",
    "",
    `Vehicle: ${v.fullTitle}`,
    `Stock number: ${stockNumber}`,
    `Price: ${formatPrice(v.price)}`,
    `Mileage: ${formatKm(v.mileage)}`,
    `Transmission: ${v.transmission}`,
    `Fuel type: ${v.fuelType}`,
    `Body type: ${v.bodyType}`,
    `Colour: ${v.color || "Please confirm"}`,
    `Vehicle page: ${window.location.origin}${vehiclePath}`,
    "",
    "Please contact me about this enquiry.",
  ].join("\n");
  const enquiryLink = contactEnquiryLink({
    subject,
    message,
    vehicle: v.fullTitle,
    enquiryType: "Vehicle availability",
  });
  const specs = [
    { icon: Calendar, value: v.year, label: "Year" },
    { icon: Gauge, value: formatKm(v.mileage), label: "Mileage" },
    { icon: GitFork, value: v.transmission, label: "Transmission" },
    { icon: Fuel, value: v.fuelType, label: "Fuel Type" },
  ];

  return (
    <article className="featured-vehicle-card" data-testid={`featured-vehicle-${v.id}`}>
      <div className="featured-vehicle-image" data-vehicle={v.id}>
        <img src={v.images[0]} alt={v.fullTitle} loading="lazy" />
        <span className="featured-vehicle-status">
          <span aria-hidden="true" /> {available ? "Available" : v.status === "sold" ? "Sold" : "Draft"}
        </span>
      </div>
      <div className="featured-vehicle-body">
        <h3>{v.fullTitle}</h3>
        <p className="featured-vehicle-price">{formatPrice(v.price)}</p>
        <dl className="featured-vehicle-specs">
          {specs.map(({ icon: Icon, value, label }) => (
            <div key={label}>
              <Icon aria-hidden="true" />
              <div><dd>{value}</dd><dt>{label}</dt></div>
            </div>
          ))}
        </dl>
        <p className="featured-vehicle-summary">{v.description || `${v.bodyType} available from ${v.location}.`}</p>
        <div className="featured-vehicle-actions">
          <Link to={vehiclePath} className="featured-primary">
            View Details <ArrowRight aria-hidden="true" size={16} />
          </Link>
          {available ? (
            <Link to={enquiryLink} className="featured-secondary">Enquire Now</Link>
          ) : (
            <Link to="/vehicles/" className="featured-secondary">View Similar</Link>
          )}
        </div>
      </div>
    </article>
  );
};
